import gsap from "../../../../helpers/gsapInstance";
import {MainPageTypes} from "../../../../types/main-page";
import ThirdScreenSwitchAngleViewTypeEnum = MainPageTypes.ThirdScreenSwitchAngleViewTypeEnum;
import ClickEvent = JQuery.ClickEvent;
import AsyncHelper from "../../../../helpers/AsyncHelper";
import MainPageCommonService from "../../../../services/main-page/MainPageCommonService";
import {Helpers} from '../../../../helpers/helpers';

export default class MainPageThirdScreenCommon extends MainPageCommonService {
    private readonly $holder: JQuery;

    public readonly $screen: JQuery;
    public readonly $screenContainer: JQuery;
    public readonly $topContent: JQuery;
    public readonly $bottomContent: JQuery;
    public readonly $mainContainer: JQuery;
    public readonly $mainBackground: JQuery;
    public readonly $primaryPointsItems: JQuery;
    public readonly $primaryPointsItemsContainer: JQuery;
    public readonly $secondaryPointsItems: JQuery;
    public readonly $secondaryPointsItemsContainer: JQuery;
    public readonly $angleViewSwitcher: JQuery;
    public readonly $indicationInList: JQuery;
    public scaleAttribute = Helpers.isMobile() ? "translate(-50%, 200%) scale(5)" : "translate(-50%, 210%) scale(5.5)"

    /* Активный тип переключения обзора (face/primary/лицо || body/secondary/тело */
    public activeSwitcherType: ThirdScreenSwitchAngleViewTypeEnum = ThirdScreenSwitchAngleViewTypeEnum.PRIMARY;

    /* Флаг перехода обзора */
    public isAngleViewSwitching: boolean = false;

    /* Стартовые проценты сдвига каждого point, чтобы поинты сдвигались лесенкой */
    public STARTED_PERCENTS: number = 30;
    /* Проценты прибавления к каждому point */
    public ADDING_PERCENTS: number = 30;

    public readonly selectors = {
        screen: ".js-main-page-mechanism-section",
        screenContainer: ".js-main-page-mechanism-section-container",
        topContent: ".js-main-page-mechanism-section-top-content",
        bottomContent: ".js-main-page-mechanism-section-bottom-content",
        mainContainer: ".js-main-page-mechanism-section-main-content-container",
        mainBackground: ".js-main-page-mechanism-section-main-content-background",
        primaryPointsItemsContainer: ".js-main-page-mechanism-section-face-points",
        primaryPointsItems: ".js-main-page-mechanism-section-face-points-item",
        secondaryPointsItemsContainer: ".js-main-page-mechanism-section-body-points",
        secondaryPointsItems: ".js-main-page-mechanism-section-body-points-item",
        angleViewSwitcher: ".js-main-page-mechanism-section-angle-view-switcher",
        indicationsInUse: '.js-main-page-mechanism-section-bottom-list',
    };

    constructor($holder: JQuery) {
        super();

        this.$holder = $holder;

        this.$screen = this.$holder.find(this.selectors.screen);
        this.$screenContainer = this.$screen.find(this.selectors.screenContainer);
        this.$topContent = this.$screenContainer.find(this.selectors.topContent);
        this.$bottomContent = this.$screenContainer.find(this.selectors.bottomContent);
        this.$mainContainer = this.$screenContainer.find(this.selectors.mainContainer);
        this.$mainBackground = this.$screenContainer.find(this.selectors.mainBackground);
        this.$primaryPointsItemsContainer = this.$screenContainer.find(this.selectors.primaryPointsItemsContainer);
        this.$primaryPointsItems = this.$primaryPointsItemsContainer.find(this.selectors.primaryPointsItems);
        this.$secondaryPointsItemsContainer = this.$screenContainer.find(this.selectors.secondaryPointsItemsContainer);
        this.$secondaryPointsItems = this.$secondaryPointsItemsContainer.find(this.selectors.secondaryPointsItems);
        this.$angleViewSwitcher = this.$holder.find(this.selectors.angleViewSwitcher);
        this.$indicationInList = this.$holder.find(this.selectors.indicationsInUse);

        this.switchAngleView = this.switchAngleView.bind(this);
        this.initEvents = this.initEvents.bind(this);
        this.setDefaultProps = this.setDefaultProps.bind(this);
        this.setStartAnimationForPoints = this.setStartAnimationForPoints.bind(this);
        this.switchAngleView = this.switchAngleView.bind(this);
        this.switchToSecondaryPoints = this.switchToSecondaryPoints.bind(this);
        this.switchToPrimaryPoints = this.switchToPrimaryPoints.bind(this);
        this.showSwitchedPoints = this.showSwitchedPoints.bind(this);
        this.hideActivePoints = this.hideActivePoints.bind(this);
    }

    /**
     * Инициализация событий
     * @public
     */
    public initEvents(): void {
        this.$angleViewSwitcher.on('click', this.switchAngleView);
    }

    /**
     * Задать дефолтные значения для третьего экрана при открытии экрана
     * @public
     */
    public async setDefaultProps(): Promise<void> {
        /* Функция обнуления поинтов, у каждого задаем this.STARTED_PERCENTS + this.ADDING_PERCENTS к каждому следующему поинту и прячем родительский элемент */
        const resetPointValues = (items: JQuery<HTMLElement>) => {
            let counterPercents = this.STARTED_PERCENTS;

            items.each((_, item) => {
                gsap.to(item, {
                    translateX: `${counterPercents}%`,
                    opacity: 0,
                    duration: 0,
                    onComplete: () => counterPercents += this.ADDING_PERCENTS
                });
            });

            items.parent().hide();
        };

        /* обнуляем поинты для двух типов (face/body) и переключаем основной background под primary(face) */
        resetPointValues(this.$primaryPointsItems);
        resetPointValues(this.$secondaryPointsItems);
        await this.switchToPrimaryBackground(true);

        /* У кнопок свитчера удаляем active класс и ставим его на первый элемент + ставим активный тип PRIMARY */
        this.$angleViewSwitcher?.children().each((_, item) => item.classList.remove(this.ACTIVE_CLASS));
        this.$angleViewSwitcher?.children()?.first()?.addClass(this.ACTIVE_CLASS);

        this.activeSwitcherType = ThirdScreenSwitchAngleViewTypeEnum.PRIMARY;
    }

    /**
     * Задать стартовую анимацию для points
     * @public
     */
    public async setStartAnimationForPoints(): Promise<void> {
        await this.showSwitchedPoints(this.$primaryPointsItems);
    }

    /**
     * Сменить угол обзора (face/primary || body/secondary)
     * @private
     * @param event
     */
    private async switchAngleView(event: ClickEvent): Promise<void> {
        if (this.isAngleViewSwitching) return;

        const target = event.target.closest("button");

        if (target && !target.classList.contains(this.ACTIVE_CLASS)) {
            this.isAngleViewSwitching = true;

            this.$angleViewSwitcher?.children().each((_, item) => item.classList.remove(this.ACTIVE_CLASS));

            const switcherType = target.dataset.switcherType as ThirdScreenSwitchAngleViewTypeEnum;

            this.activeSwitcherType = switcherType;
            this.$mainContainer.data('type', switcherType);
            target.classList.add(this.ACTIVE_CLASS);

            if (switcherType === ThirdScreenSwitchAngleViewTypeEnum.PRIMARY) {
                await this.switchToPrimaryAngleView();
            } else {
                await this.switchToSecondaryAngleView();
            }

            this.isAngleViewSwitching = false;
        }
    }

    private async switchListLast() {
        await this.hideActiveList(this.$indicationInList.eq(1).find('li'));
        await this.showSwitchedActiveList(this.$indicationInList.eq(0).find('li'));
    }

    private async switchListFirst() {
        await this.hideActiveList(this.$indicationInList.eq(0).find('li'));
        await this.showSwitchedActiveList(this.$indicationInList.eq(1).find('li'));
    }

    private async hideActiveList(activeList: JQuery<HTMLElement>): Promise<void> {
        const parentContainer = activeList.closest(this.selectors.indicationsInUse);

        let counterPercents = this.STARTED_PERCENTS;

        activeList.each((_, item) => {
            gsap.to(item, {
                translateX: `-${counterPercents}%`,
                opacity: 0,
                delay: this.ONE_FIFTH_ANIMATION_DURATION / 2,
            })
        });
        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS / 2, true);

        parentContainer.hide();
    }

    private async showSwitchedActiveList(activeList: JQuery<HTMLElement>): Promise<void> {
        const parentContainer = activeList.closest(this.selectors.indicationsInUse);
        parentContainer.show();

        activeList.each((_, item) => {
            gsap.to(item, {
                translateX: 0,
                opacity: 1
            })
        })
    }

    /**
     * Сменить угол обзора на основной (face/primary)
     * @private
     */
    private async switchToPrimaryAngleView(): Promise<void> {
        await this.switchToPrimaryBackground();
        await this.switchToPrimaryPoints();
        await this.switchListLast();

        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
    }

    /**
     * Сменить угол обзора на дополнительный (body/secondary)
     * @private
     */
    private async switchToSecondaryAngleView(): Promise<void> {
        await this.switchToSecondaryBackground();
        await this.switchToSecondaryPoints();
        await this.switchListFirst();
        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
    }

    /**
     * Сменить картинку на основную (face/primary)
     * @private
     * @param isResetToDefault
     * если isResetToDefault, то мы убираем задержку и анимация становится мгновенной
     */
    public async switchToPrimaryBackground(isResetToDefault: boolean = false): Promise<void> {
        gsap.to(this.$mainBackground, {
            transform: this.scaleAttribute,
            duration: isResetToDefault ? 0 : this.ANIMATION_DURATION_IN_SECONDS
        });
    }

    /**
     * Сменить картинку на дополнительную с отдалением (body/secondary)
     * @private
     */
    private async switchToSecondaryBackground(): Promise<void> {
        gsap.to(this.$mainBackground, {
            transform: "translate(-50%, 0%) scale(1)",
            duration: this.ANIMATION_DURATION_IN_SECONDS
        });

    }

    /**
     * Сменить точки на основные (face/primary)
     * @private
     */
    private async switchToPrimaryPoints(): Promise<void> {
        await this.hideActivePoints(this.$secondaryPointsItems);
        await this.showSwitchedPoints(this.$primaryPointsItems);
    }

    /**
     * Сменить точки на дополнительные (body/secondary)
     * @private
     */
    private async switchToSecondaryPoints(): Promise<void> {
        await this.hideActivePoints(this.$primaryPointsItems);
        await this.showSwitchedPoints(this.$secondaryPointsItems);
    }

    /**
     * Показать новые точки с анимацией
     * @private
     * @param activePoints
     * activePoints - массив элементов с точками
     */
    private async showSwitchedPoints(activePoints: JQuery<HTMLElement>): Promise<void> {
        const parentContainer = activePoints.parent();
        parentContainer.show();

        activePoints.each((_, item) => {
            gsap.to(item, {
                translateX: 0,
                opacity: 1
            })
        })
    }

    /**
     * Скрыть текущие точки с анимацией
     * @private
     * @param activePoints
     * activePoints - массив элементов с точками
     */
    private async hideActivePoints(activePoints: JQuery<HTMLElement>): Promise<void> {
        const parentContainer = activePoints.parent();

        let counterPercents = this.STARTED_PERCENTS;

        activePoints.each((_, item) => {
            gsap.to(item, {
                translateX: `${counterPercents}%`,
                opacity: 0,
                delay: this.ONE_FIFTH_ANIMATION_DURATION / 2,
                onComplete: () => counterPercents += this.ADDING_PERCENTS
            })
        });
        await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS / 2, true);

        parentContainer.hide();
    }
}
